<template>
    <div class="shared-report-page">
        <div class="report-container">
            <!-- Header Card -->
            <div class="report-card header-card">
                <div class="brand-section">
                    <img src="/static/favicon/favicon-32x32.png" alt="MegaBull" class="brand-logo">
                    <span class="brand-name">MegaBull</span>
                </div>
                <h1 class="report-title">Monthly Report</h1>
                <div class="user-info" v-if="data.userName">
                    <div class="user-avatar">
                        <i class="bi bi-person-circle"></i>
                    </div>
                    <div class="user-details">
                        <span class="user-name">{{ data.userName }}</span>
                        <span class="date-range">{{ formattedFromDate }} - {{ formattedToDate }}</span>
                    </div>
                </div>
            </div>

            <!-- Error Message -->
            <div class="error-card" v-if="errorMsg">
                <i class="bi bi-exclamation-triangle"></i>
                <span>{{ errorMsg }}</span>
            </div>

            <!-- P&L Stats Section -->
            <div v-if="data.response && data.response.plAfterTax !== undefined">
                <div class="section-header">
                    <i class="bi bi-graph-up"></i>
                    <span>P&L Statistics</span>
                </div>

                <div class="stats-grid">
                    <div class="stat-card" :class="data.response.plAfterTax >= 0 ? 'positive' : 'negative'">
                        <div class="stat-icon">
                            <i class="bi bi-cash-stack"></i>
                        </div>
                        <div class="stat-content">
                            <span class="stat-label">Profit After Tax</span>
                            <span class="stat-value">{{ data.response.plAfterTax.toLocaleString('en-IN') }} &#8377;</span>
                        </div>
                    </div>

                    <div class="stat-card" :class="data.response.plBeforeTax >= 0 ? 'positive' : 'negative'">
                        <div class="stat-icon">
                            <i class="bi bi-wallet2"></i>
                        </div>
                        <div class="stat-content">
                            <span class="stat-label">Profit Before Tax</span>
                            <span class="stat-value">{{ data.response.plBeforeTax.toLocaleString('en-IN') }} &#8377;</span>
                        </div>
                    </div>

                    <div class="stat-card expense">
                        <div class="stat-icon">
                            <i class="bi bi-receipt"></i>
                        </div>
                        <div class="stat-content">
                            <span class="stat-label">Tax Paid</span>
                            <span class="stat-value">{{ data.response.tax.toLocaleString('en-IN') }} &#8377;</span>
                        </div>
                    </div>

                    <div class="stat-card expense">
                        <div class="stat-icon">
                            <i class="bi bi-percent"></i>
                        </div>
                        <div class="stat-content">
                            <span class="stat-label">Brokerage</span>
                            <span class="stat-value">{{ data.response.brokerage.toLocaleString('en-IN') }} &#8377;</span>
                        </div>
                    </div>
                </div>

                <!-- Daily P&L Section -->
                <div class="section-header">
                    <i class="bi bi-calendar3"></i>
                    <span>Daily P&L</span>
                </div>

                <div class="daily-grid">
                    <div class="daily-card" v-for="(x, index) in data.response.reportDayList" :key="index" :class="x.pl >= 0 ? 'positive' : 'negative'">
                        <span class="daily-date">{{ formatDate(x.day) }}</span>
                        <span class="daily-value">{{ x.pl >= 0 ? '+' : '' }}{{ x.pl.toLocaleString('en-IN') }} &#8377;</span>
                    </div>
                </div>
            </div>

            <!-- CTA Section -->
            <div class="cta-card">
                <p class="cta-text">Interested in paper trading?</p>
                <a href="https://trade.megabull.in/signup" target="_blank" class="cta-button">
                    <i class="bi bi-rocket-takeoff"></i>
                    Sign Up for MegaBull
                </a>
            </div>
        </div>
    </div>
</template>

<script>
import { getMonthlySharedReport } from '@/service/restService'
export default {
  name: 'SharedMonthReport',
  components: {},
  data () {
    return {
      reportId: '',
      data: {},
      errorMsg: ''
    }
  },
  computed: {
    formattedFromDate () {
      if (!this.data.startDate) return ''
      return new Date(this.data.startDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    formattedToDate () {
      if (!this.data.endDate) return ''
      return new Date(this.data.endDate).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    }
  },
  methods: {
    getMonthlySharedReport: function (uuid) {
      this.errorMsg = ''
      getMonthlySharedReport(uuid).then((response) => {
        this.data = response.data
      }).catch((error) => {
        this.data = {}
        this.errorMsg = error.response.data.message[0]
      })
    },
    formatDate (dateStr) {
      return new Date(dateStr).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' })
    }
  },
  mounted () {
    const urlParams = new URLSearchParams(window.location.search)
    this.reportId = urlParams.get('id') || 'Not provided'
    this.getMonthlySharedReport(this.reportId)
  }
}
</script>

<style scoped>
.shared-report-page {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.report-container {
  max-width: 800px;
  width: 100%;
}

/* Header Card */
.report-card {
  background: var(--bs-body-bg);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

.header-card {
  text-align: center;
}

.brand-section {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-bottom: 16px;
}

.brand-logo {
  width: 32px;
  height: 32px;
}

.brand-name {
  font-size: 20px;
  font-weight: 700;
  color: var(--bs-body-color);
}

.report-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--bs-body-color);
  margin: 0 0 20px 0;
}

.user-info {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 16px;
  background: var(--bs-tertiary-bg);
  border-radius: 12px;
}

.user-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea, #764ba2);
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-avatar i {
  font-size: 24px;
  color: white;
}

.user-details {
  display: flex;
  flex-direction: column;
  text-align: left;
}

.user-name {
  font-size: 16px;
  font-weight: 600;
  color: var(--bs-body-color);
}

.date-range {
  font-size: 13px;
  color: var(--bs-secondary-color);
}

/* Error Card */
.error-card {
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.2);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #dc3545;
}

.error-card i {
  font-size: 20px;
}

/* Section Header */
.section-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 16px;
  padding: 12px 16px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
}

.section-header i {
  font-size: 18px;
  color: white;
}

.section-header span {
  font-size: 14px;
  font-weight: 600;
  color: white;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 24px;
}

.stat-card {
  background: var(--bs-body-bg);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.stat-card.positive {
  border-left: 4px solid #0BDA51;
}

.stat-card.negative {
  border-left: 4px solid #EF0107;
}

.stat-card.expense {
  border-left: 4px solid #ffc107;
}

.stat-icon {
  width: 44px;
  height: 44px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-card.positive .stat-icon {
  background: rgba(11, 218, 81, 0.1);
  color: #0BDA51;
}

.stat-card.negative .stat-icon {
  background: rgba(239, 1, 7, 0.1);
  color: #EF0107;
}

.stat-card.expense .stat-icon {
  background: rgba(255, 193, 7, 0.1);
  color: #ffc107;
}

.stat-icon i {
  font-size: 20px;
}

.stat-content {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.stat-label {
  font-size: 12px;
  color: var(--bs-secondary-color);
}

.stat-value {
  font-size: 16px;
  font-weight: 700;
  color: var(--bs-body-color);
}

.stat-card.positive .stat-value {
  color: #0BDA51;
}

.stat-card.negative .stat-value {
  color: #EF0107;
}

/* Daily Grid */
.daily-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 10px;
  margin-bottom: 24px;
}

.daily-card {
  background: var(--bs-body-bg);
  border-radius: 10px;
  padding: 12px;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.daily-card.positive {
  border-top: 3px solid #0BDA51;
}

.daily-card.negative {
  border-top: 3px solid #EF0107;
}

.daily-date {
  font-size: 12px;
  font-weight: 600;
  color: var(--bs-secondary-color);
}

.daily-value {
  font-size: 14px;
  font-weight: 700;
}

.daily-card.positive .daily-value {
  color: #0BDA51;
}

.daily-card.negative .daily-value {
  color: #EF0107;
}

/* CTA Card */
.cta-card {
  background: var(--bs-body-bg);
  border-radius: 16px;
  padding: 24px;
  text-align: center;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
}

.cta-text {
  font-size: 14px;
  color: var(--bs-secondary-color);
  margin: 0 0 16px 0;
}

.cta-button {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border-radius: 10px;
  text-decoration: none;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s;
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
  color: white;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .shared-report-page {
    padding: 12px;
    align-items: flex-start;
  }

  .report-card {
    padding: 20px;
  }

  .report-title {
    font-size: 20px;
  }

  .user-info {
    flex-direction: column;
    text-align: center;
  }

  .user-details {
    text-align: center;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .stat-card {
    padding: 14px;
  }

  .stat-icon {
    width: 40px;
    height: 40px;
  }

  .stat-icon i {
    font-size: 18px;
  }

  .stat-value {
    font-size: 15px;
  }

  .daily-grid {
    grid-template-columns: repeat(3, 1fr);
  }

  .daily-card {
    padding: 10px;
  }

  .daily-date {
    font-size: 11px;
  }

  .daily-value {
    font-size: 12px;
  }
}

@media (max-width: 480px) {
  .daily-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>



// WEBPACK FOOTER //
// src/components/report/SharedMonthReport.vue